import { useEffect, useRef, useState } from "react";
import api from "../../services/api";

interface DeliveryFile {
    id: string;
    name: string;
    sizeBytes: number;
    createdAt: string;
    offerIds: string[];
}

interface Offer {
    id: string;
    name: string;
    status?: string;
    price?: string | number;
    billingType?: string;
}

const MAX_BYTES = 50 * 1024 * 1024;

function formatPrice(value?: string | number) {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value ?? 0));
}

function formatSize(bytes: number) {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * Aba "Entrega" do produto e-book: o seller sobe os PDFs e escolhe o que cada oferta entrega.
 * Depois da compra o comprador recebe um e-mail com o botão de download de cada PDF.
 * Oferta sem nenhum PDF marcado entrega todos os PDFs do produto.
 */
export default function EbookDeliveryTab({ productId }: { productId: string }) {
    const [files, setFiles] = useState<DeliveryFile[]>([]);
    const [offers, setOffers] = useState<Offer[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [savingOffer, setSavingOffer] = useState<string | null>(null);
    const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const load = async () => {
        try {
            const [filesRes, offersRes] = await Promise.all([
                api.get(`/api/ebook-delivery/products/${productId}/files`),
                api.get(`/api/products/${productId}/offers-with-links`),
            ]);
            setFiles(filesRes.data.data || []);
            setOffers((offersRes.data.offers || []).filter((o: Offer) => o.status !== "deleted"));
        } catch (err: any) {
            setMessage({ type: "error", text: err?.response?.data?.message || "Erro ao carregar a entrega" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [productId]);

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = Array.from(e.target.files || []);
        e.target.value = "";
        if (!selected.length) return;
        setMessage(null);

        for (const file of selected) {
            if (!/\.pdf$/i.test(file.name)) {
                setMessage({ type: "error", text: `${file.name}: envie apenas arquivos PDF` });
                continue;
            }
            if (file.size > MAX_BYTES) {
                setMessage({ type: "error", text: `${file.name}: o PDF pode ter no máximo 50MB` });
                continue;
            }
            setUploading(true);
            try {
                const form = new FormData();
                form.append("file", file);
                const res = await api.post(`/api/ebook-delivery/products/${productId}/files`, form, {
                    headers: { "Content-Type": "multipart/form-data" },
                    timeout: 180000,
                });
                setFiles(prev => [...prev, res.data.data]);
            } catch (err: any) {
                setMessage({ type: "error", text: err?.response?.data?.message || `Erro ao enviar ${file.name}` });
            } finally {
                setUploading(false);
            }
        }
    };

    const handleRemove = async (file: DeliveryFile) => {
        if (!window.confirm(`Remover "${file.name}"? Quem já comprou continua conseguindo baixar pelo e-mail recebido.`)) return;
        try {
            await api.delete(`/api/ebook-delivery/products/${productId}/files/${file.id}`);
            setFiles(prev => prev.filter(f => f.id !== file.id));
        } catch (err: any) {
            setMessage({ type: "error", text: err?.response?.data?.message || "Erro ao remover o arquivo" });
        }
    };

    const toggleOfferFile = async (offerId: string, fileId: string) => {
        const current = files.filter(f => f.offerIds.includes(offerId)).map(f => f.id);
        const next = current.includes(fileId) ? current.filter(id => id !== fileId) : [...current, fileId];
        setSavingOffer(offerId);
        setMessage(null);
        try {
            await api.put(`/api/ebook-delivery/products/${productId}/offers/${offerId}/files`, { fileIds: next });
            setFiles(prev => prev.map(f => ({
                ...f,
                offerIds: next.includes(f.id)
                    ? Array.from(new Set([...f.offerIds, offerId]))
                    : f.offerIds.filter(id => id !== offerId),
            })));
        } catch (err: any) {
            setMessage({ type: "error", text: err?.response?.data?.message || "Erro ao salvar a oferta" });
        } finally {
            setSavingOffer(null);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h3 className="text-base font-medium text-gray-900 dark:text-white">Arquivos do e-book</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Após a compra, o comprador recebe um e-mail com o botão para baixar cada PDF. Sem área de membros.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading}
                        className="h-11 shrink-0 rounded-lg bg-brand-500 px-5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
                    >
                        {uploading ? "Enviando..." : "Adicionar PDF"}
                    </button>
                    <input ref={inputRef} type="file" accept="application/pdf,.pdf" multiple hidden onChange={handleUpload} />
                </div>

                {files.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                        Nenhum PDF ainda. Adicione o arquivo que o comprador vai receber (até 50MB).
                    </p>
                ) : (
                    <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                        {files.map(f => (
                            <li key={f.id} className="flex items-center justify-between py-3">
                                <div>
                                    <p className="text-sm font-medium text-gray-800 dark:text-white/90">📘 {f.name}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{formatSize(f.sizeBytes)}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleRemove(f)}
                                    className="text-sm text-red-600 hover:underline dark:text-red-400"
                                >
                                    Remover
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {files.length > 0 && offers.length > 0 && (
                <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">O que cada oferta entrega</h3>
                    <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                        Oferta sem nenhum PDF marcado entrega todos. No order bump, vale o que estiver marcado na oferta padrão deste produto.
                    </p>
                    <div className="flex flex-col gap-4">
                        {offers.map(offer => {
                            const marked = files.filter(f => f.offerIds.includes(offer.id));
                            return (
                                <div key={offer.id} className="rounded-lg border border-gray-200 p-4 dark:border-gray-800">
                                    <div className="mb-2 flex items-center justify-between">
                                        <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                                            {offer.name}
                                            <span className="ml-2 font-normal text-gray-500 dark:text-gray-400">
                                                {formatPrice(offer.price)}{offer.billingType === "recurring" ? " · assinatura" : ""}
                                            </span>
                                        </p>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                            {savingOffer === offer.id
                                                ? "Salvando..."
                                                : marked.length === 0 ? "Entrega todos os PDFs" : `Entrega ${marked.length} de ${files.length}`}
                                        </span>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        {files.map(f => (
                                            <label key={f.id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                                <input
                                                    type="checkbox"
                                                    checked={f.offerIds.includes(offer.id)}
                                                    disabled={savingOffer === offer.id}
                                                    onChange={() => toggleOfferFile(offer.id, f.id)}
                                                />
                                                {f.name}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {message && (
                <p className={`text-sm ${message.type === "success" ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                    {message.text}
                </p>
            )}
        </div>
    );
}
